import React, {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { COLORS, SCORE_THRESHOLDS } from '../../../../constants/themes';

const warehouseNavigatorLogo = require('../../../../assets/warehouse_navigator.png');

export type ChartDataPoint = {
	date: string;
	value: number;
};

export type SparcKpiPrintCard = {
	id: string;
	title: string;
	badgeLetter: string;
	badgeColor: string;
	actual: number;
	target: number;
	delta: number;
	chartData: ChartDataPoint[];
};

export type LighthouseKpiPrintCard = {
	id: string;
	title: string;
	cw: number | string;
	lcw: number | string;
	lcp: number | string;
	ytd: number | string;
	chartData: ChartDataPoint[];
};

export interface ReviewProgressKpiPrintRef {
	print: () => void;
	printOnePerPage: () => void;
	getHtml?: () => string;
}

const escapeHtml = (text: string): string => {
	const div = typeof document !== 'undefined' ? document.createElement('div') : null;
	if (div) {
		div.textContent = String(text ?? '');
		return div.innerHTML;
	}

	return String(text ?? '').replace(/[&<>"']/g, (m) =>
		(
			{
				'&': '&amp;',
				'<': '&lt;',
				'>': '&gt;',
				'"': '&quot;',
				"'": '&#39;',
			} as const
		)[m] || m,
	);
};

const formatNumber = (value: number | string): string => {
	const num = Number(value);
	if (!Number.isFinite(num)) return '0.00';
	return num.toFixed(2);
};

// Match LighthouseKpiCard: green/red metric value based on thresholds
const getColorForScore = (value: number | string): string => {
	let numericValue: number;
	if (typeof value === 'string') {
		numericValue = parseFloat(value.toString().replace('%', ''));
	} else {
		numericValue = value as number;
	}

	if (isNaN(numericValue)) {
		return '#000000';
	}

	if (numericValue >= SCORE_THRESHOLDS.ROUNDUPHIGH) {
		return COLORS.SUCCESS;
	}
	return COLORS.ERROR;
};

const formatScoreDisplay = (value: number | string): string => {
	const raw = String(value ?? '');
	if (!raw) return '';
	return raw.includes('%') ? raw : `${raw}%`;
};

const getOrderedChartData = (chartData: ReadonlyArray<ChartDataPoint>): ChartDataPoint[] => {
	if (!chartData || chartData.length === 0) return [];
	const parsed = chartData.map((d, i) => ({ ...d, _ts: Date.parse(d.date), _idx: i }));
	const allValid = parsed.every((p) => !isNaN(p._ts));
	if (allValid) {
		return parsed
			.sort((a, b) => a._ts - b._ts)
			.map(({ _ts, _idx, ...rest }) => rest);
	}
	return [...chartData].reverse();
};

const renderStyles = (): string => {
	return `
		@page { margin: 10mm; }

		html, body { padding: 0; margin: 0; }
		body {
			font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
			color: #1C1C1C;
			background: #FFFFFF;
		}

		* { -webkit-print-color-adjust: exact; print-color-adjust: exact; box-sizing: border-box; }

		table.report-table { width: 100%; border-collapse: collapse; }
		thead { display: table-header-group; }
		tfoot { display: table-footer-group; }
		tr { page-break-inside: avoid; break-inside: avoid; }

		tr.section-header-row { page-break-after: avoid; break-after: avoid; }

		.print-header-cell {
			padding: 0 0 10px 0;
			border-bottom: 0;
			text-align: left;
			font-weight: normal;
		}

		.header-bar {
			display: flex;
			align-items: center;
			justify-content: space-between;
			border: 1px solid #DADADA;
			border-radius: 6px;
			padding: 8px 12px;
			background: #FFFFFF;
		}
		.header-left { display: flex; align-items: center; gap: 8px; min-width: 0; }
		.logo { width: 20px; height: 20px; object-fit: contain; border-radius: 4px; flex-shrink: 0; }
		.header-title { font-weight: 700; font-size: 16px; color: #093F6E; white-space: nowrap; }
		.header-date { font-size: 13px; font-weight: 400; color: #6B6B6B; white-space: nowrap; }

		.section-row {
			display: flex;
			align-items: center;
			justify-content: space-between;
			padding: 10px 0 12px;
		}
		.section-title { font-weight: 700; font-size: 14px; letter-spacing: 0.2px; }

		tr.cards-row { page-break-inside: avoid; break-inside: avoid; }
		td.cards-cell { padding: 12px 0 0; }

		.kpi-row {
			display: flex;
			gap: 12px;
			width: 100%;
			page-break-inside: avoid;
			break-inside: avoid;
		}

		.kpi-card {
			flex: 1;
			background: #FFFFFF;
			border-radius: 16px;
			border: 1px solid #E0E7FF;
			padding: 16px;
			page-break-inside: avoid;
			break-inside: avoid;
		}
		.kpi-card.placeholder { visibility: hidden; }

		.card-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
		.card-header-left { display: flex; align-items: center; gap: 12px; min-width: 0; }
		.badge { width: 24px; height: 24px; border-radius: 4px; display: flex; align-items: center; justify-content: center; }
		.badge-text { font-size: 14px; font-weight: 700; color: #1B5E20; }
		.card-title { font-size: 16px; font-weight: 500; color: #2D2D2D; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

		.metrics {
			display: flex;
			justify-content: space-around;
			border-radius: 16px;
			border: 1px solid #E3F2FD;
			padding: 12px 2px;
			margin-bottom: 10px;
		}
		.metric { flex: 1; text-align: center; }
		.metric-label { font-size: 12px; font-weight: 400; color: #4B4B4B; margin-bottom: 8px; }
		.metric-value { font-size: 16px; font-weight: 700; color: #000000; }

		.chart { display: flex; gap: 8px; }
		.y-axis { display: flex; flex-direction: column; justify-content: space-between; align-items: flex-end; margin-top: -6px; margin-bottom: 22px; }
		.y-label { font-size: 12px; font-weight: 400; color: #616161; }

		.chart-content { flex: 1; }
		.bars {
			display: flex;
			align-items: flex-end;
			height: 72px;
			border-bottom: 1px solid #E0E0E0;
			gap: 4px;
		}
		.bar-wrapper { flex: 1; min-width: 20px; display: flex; justify-content: center; align-items: flex-end; height: 100%; }
		.bar {
			width: 55%;
			min-height: 2px;
			background: #41B8E6;
			border-top-left-radius: 4px;
			border-top-right-radius: 4px;
		}
		.x-axis { display: flex; gap: 4px; margin-top: 8px; }
		.x-label-wrapper { flex: 1; min-width: 20px; text-align: center; }
		.x-label { font-size: 12px; font-weight: 400; color: #616161; }

		.chart-legend { margin-top: 6px; display: flex; align-items: center; gap: 6px; }
		.legend-swatch { width: 10px; height: 10px; border-radius: 2px; background: #41B8E6; }
		.legend-text { font-size: 10px; color: #616161; }

		/* Portrait: keep vertical stacked cards that fit within the page width */
		@media print and (orientation: portrait) {
			.report-table { width: auto; }
			.kpi-row { flex-wrap: wrap; gap: 12px; width: 100%; }
			.kpi-card { flex: 1 1 0; max-width: 100%; min-width: 0; }
			.card-title { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
			.header-bar { width: 100%; }
		}

		/* Landscape: expand layout to use full page width without squishing chart bars */
		@media print and (orientation: landscape) {
			.report-table { width: 100%; }
			.kpi-row { flex-wrap: nowrap; gap: 12px; width: 100%; }
			.kpi-card {
				flex: 1 1 0;
				max-width: none;
				min-width: 0;
				box-sizing: border-box;
				page-break-inside: avoid;
				break-inside: avoid;
				-webkit-column-break-inside: avoid;
			}
			.header-bar { width: 100%; }
		}
	`;
};

const renderPrintHeader = (opts: { date: string; logoBase64?: string }): string => {
	const logoImg = opts.logoBase64
		? `<img class="logo" src="${opts.logoBase64}" alt="Warehouse Navigator" />`
		: `<div class="logo" style="background: transparent;"></div>`;

	return `
		<tr>
			<th class="print-header-cell">
				<div class="header-bar">
					<div class="header-left">
						${logoImg}
						<span class="header-title">Warehouse Navigator</span>
					</div>
					<span class="header-date">${escapeHtml(opts.date)}</span>
				</div>
			</th>
		</tr>
	`;
};

const renderSectionHeader = (title: string): string => {
	return `
		<tr class="section-header-row">
			<th style="padding:0; text-align:left; font-weight: normal;">
				<div class="section-row">
					<span class="section-title">${escapeHtml(title)}</span>
				</div>
			</th>
		</tr>
	`;
};

const renderChart = (
	ordered: ReadonlyArray<ChartDataPoint>,
	maxValue: number,
): string => {
	const barsParts: string[] = [];
	const xLabelParts: string[] = [];

	for (const dp of ordered) {
		const pct = maxValue > 0 ? (Number(dp.value) / maxValue) * 100 : 0;
		const safePct = Number.isFinite(pct) ? Math.max(0, pct) : 0;
		barsParts.push(`
			<div class="bar-wrapper">
				<div class="bar" style="height: ${safePct.toFixed(2)}%;"></div>
			</div>
		`);
		xLabelParts.push(
			`<div class="x-label-wrapper"><span class="x-label">${escapeHtml(dp.date)}</span></div>`,
		);
	}

	const maxLabel = Number.isInteger(maxValue) ? String(maxValue) : maxValue.toFixed(2);
	return `
		<div class="chart">
			<div class="y-axis">
				<span class="y-label">${maxLabel}</span>
				<span class="y-label">0</span>
			</div>
			<div class="chart-content">
				<div class="bars">${barsParts.join('')}</div>
				<div class="x-axis">${xLabelParts.join('')}</div>
			</div>
		</div>
	`;
};

const renderSparcCard = (card: SparcKpiPrintCard): string => {
	const ordered = getOrderedChartData(card.chartData || []);

	let maxValue = 10;
	for (const dp of ordered) {
		const v = Number(dp.value) || 0;
		if (v > maxValue) maxValue = v;
	}

	// Determine delta color and arrow based on value
	const isDeltaNegative = card.delta < 0;
	const isDeltaPositive = card.delta > 0;
	const deltaColor = isDeltaNegative ? '#D32F2F' : isDeltaPositive ? 'rgb(18, 159, 20)' : '#000000';
	
	const downArrowSvg = `<svg width="5" height="5" viewBox="0 0 5 3" fill="none" style="display:inline; margin-left:4px; vertical-align:middle;">
		<path d="M0 0L2.5 2.5L5 0H0Z" fill="${deltaColor}"/>
	</svg>`;
	
	const upArrowSvg = `<svg width="5" height="5" viewBox="0 0 5 3" fill="none" style="display:inline; margin-left:4px; vertical-align:middle;">
		<path d="M5 2.5L2.5 0L0 2.5H5Z" fill="${deltaColor}"/>
	</svg>`;
	
	const deltaArrow = isDeltaNegative ? downArrowSvg : isDeltaPositive ? upArrowSvg : '';

	return `
		<div class="kpi-card">
			<div class="card-header">
				<div class="card-header-left">
					<div class="badge" style="background:${escapeHtml(card.badgeColor || '#D4F4DD')}">
						<span class="badge-text">${escapeHtml(card.badgeLetter || '')}</span>
					</div>
					<span class="card-title">${escapeHtml(card.title || '')}</span>
				</div>
			</div>
			<div class="metrics">
				<div class="metric">
					<div class="metric-label">Actual</div>
					<div class="metric-value">${formatNumber(card.actual)}</div>
				</div>
				<div class="metric">
					<div class="metric-label">Target</div>
					<div class="metric-value">${formatNumber(card.target)}</div>
				</div>
				<div class="metric">
					<div class="metric-label">Delta</div>
					<div class="metric-value" style="color:${deltaColor}">${deltaArrow}${formatNumber(card.delta)}</div>
				</div>
			</div>
			${renderChart(ordered, maxValue)}
		</div>
	`;
};

const renderLighthouseCard = (card: LighthouseKpiPrintCard): string => {
	const ordered = getOrderedChartData(card.chartData || []);

	let maxValue = 10;
	for (const dp of ordered) {
		const v = Number(dp.value) || 0;
		if (v > maxValue) maxValue = v;
	}

	const cwColor = getColorForScore(card.cw);
	const lcwColor = getColorForScore(card.lcw);
	const lcpColor = getColorForScore(card.lcp);
	const ytdColor = getColorForScore(card.ytd);

	return `
		<div class="kpi-card">
			<div class="card-header">
				<div class="card-header-left">
					<span class="card-title">${escapeHtml(card.title || '')}</span>
				</div>
			</div>
			<div class="metrics">
				<div class="metric">
					<div class="metric-label">CW</div>
					<div class="metric-value" style="color:${escapeHtml(cwColor)}">${escapeHtml(
						formatScoreDisplay(card.cw),
					)}</div>
				</div>
				<div class="metric">
					<div class="metric-label">LCW</div>
					<div class="metric-value" style="color:${escapeHtml(lcwColor)}">${escapeHtml(
						formatScoreDisplay(card.lcw),
					)}</div>
				</div>
				<div class="metric">
					<div class="metric-label">LCP</div>
					<div class="metric-value" style="color:${escapeHtml(lcpColor)}">${escapeHtml(
						formatScoreDisplay(card.lcp),
					)}</div>
				</div>
				<div class="metric">
					<div class="metric-label">YTD</div>
					<div class="metric-value" style="color:${escapeHtml(ytdColor)}">${escapeHtml(
						formatScoreDisplay(card.ytd),
					)}</div>
				</div>
			</div>
			${renderChart(ordered, maxValue)}
		</div>
	`;
};

const renderCardGrid = (cardsHtml: string[], perRow: number, sectionHeader?: string): string => {
	const rows: string[][] = [];
	for (let i = 0; i < cardsHtml.length; i += perRow) {
		rows.push(cardsHtml.slice(i, i + perRow));
	}

	const rowHtml = rows
		.map((row) => {
			const placeholdersNeeded = Math.max(0, perRow - row.length);
			const placeholders = Array.from({ length: placeholdersNeeded })
				.map(() => `<div class="kpi-card placeholder"></div>`)
				.join('');

			return `
				<tr class="cards-row">
					<td class="cards-cell">
						<div class="kpi-row">
							${row.join('')}
							${placeholders}
						</div>
					</td>
				</tr>
			`;
		})
		.join('');

	return `<tbody>${sectionHeader ?? ''}${rowHtml}</tbody>`;
};

export const generateReviewProgressKpiPrintHTML = (args: {
	date: string;
	logoBase64?: string;
	sparcCards: SparcKpiPrintCard[];
	lighthouseCards: LighthouseKpiPrintCard[];
}): string => {
	const headerHtml = `
		<thead>
			${renderPrintHeader({ date: args.date, logoBase64: args.logoBase64 })}
		</thead>
	`;

	const hasSparcCards = (args.sparcCards?.length ?? 0) > 0;
	const hasLighthouseCards = (args.lighthouseCards?.length ?? 0) > 0;

	// Section headers are merged into their card grid <tbody> so a page break
	// cannot orphan the section title from its cards.
	const sparcGridHtml = hasSparcCards
		? renderCardGrid(
				(args.sparcCards || []).map(renderSparcCard),
				3,
				renderSectionHeader('Selected SPARC KPIs'),
			)
		: '';

	const lighthouseGridHtml = hasLighthouseCards
		? renderCardGrid(
				(args.lighthouseCards || []).map(renderLighthouseCard),
				3,
				renderSectionHeader('Selected Lighthouse KPIs'),
			)
		: '';

	return `<!DOCTYPE html>
<html lang="en">
	<head>
		<meta charset="UTF-8" />
		<meta name="viewport" content="width=device-width, initial-scale=1.0" />
		<title>Selected KPIs Print</title>
		<style>
			${renderStyles()}
		</style>
	</head>
	<body>
		<table class="report-table">
			${headerHtml}
			${sparcGridHtml}
			${lighthouseGridHtml}
		</table>
	</body>
</html>`;
};

const renderOnePerPageStyles = (): string => {
	return `
		@page { margin: 10mm; }

		html, body { padding: 0; margin: 0; }
		body {
			font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
			color: #1C1C1C;
			background: #FFFFFF;
		}
		* { -webkit-print-color-adjust: exact; print-color-adjust: exact; box-sizing: border-box; }

		.opp-page {
			display: flex;
			flex-direction: column;
			padding: 0;
			page-break-after: always;
			break-after: page;
		}
		.opp-page:last-child {
			page-break-after: avoid;
			break-after: avoid;
		}
		.opp-card-center {
			flex: 1;
			display: flex;
			align-items: center;
		}

		.header-bar {
			display: flex;
			align-items: center;
			justify-content: space-between;
			border: 1px solid #DADADA;
			border-radius: 6px;
			padding: 8px 12px;
			background: #FFFFFF;
			margin-bottom: 16px;
		}
		.header-left { display: flex; align-items: center; gap: 8px; }
		.logo { width: 20px; height: 20px; object-fit: contain; border-radius: 4px; flex-shrink: 0; }
		.header-title { font-weight: 700; font-size: 16px; color: #093F6E; }
		.header-date { font-size: 13px; font-weight: 400; color: #6B6B6B; }

		.section-row {
			display: flex;
			align-items: center;
			padding: 0 0 16px;
		}
		.section-title { font-weight: 700; font-size: 14px; letter-spacing: 0.2px; }

		.kpi-card-full {
			width: 100%;
			background: #FFFFFF;
			border-radius: 16px;
			border: 1px solid #E0E7FF;
			padding: 32px;
			display: flex;
			flex-direction: column;
		}

		.card-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; }
		.card-header-left { display: flex; align-items: center; gap: 16px; }
		.badge { width: 36px; height: 36px; border-radius: 6px; display: flex; align-items: center; justify-content: center; }
		.badge-text { font-size: 20px; font-weight: 700; color: #1B5E20; }
		.card-title { font-size: 24px; font-weight: 600; color: #2D2D2D; }

		.metrics {
			display: flex;
			justify-content: space-around;
			border-radius: 16px;
			border: 1px solid #E3F2FD;
			padding: 24px 8px;
			margin-bottom: 32px;
		}
		.metric { flex: 1; text-align: center; }
		.metric-label { font-size: 16px; font-weight: 400; color: #4B4B4B; margin-bottom: 12px; }
		.metric-value { font-size: 28px; font-weight: 700; color: #000000; }

		.chart { display: flex; gap: 12px; flex: 1; }
		.y-axis { display: flex; flex-direction: column; justify-content: space-between; align-items: flex-end; margin-top: -6px; margin-bottom: 48px; }
		.y-label { font-size: 13px; font-weight: 400; color: #616161; }

		.chart-content { flex: 1; position: relative; }
		.bars {
			display: flex;
			align-items: flex-end;
			aspect-ratio: 5 / 2;
			height: auto;
			width: 100%;
			position: relative;
			border-bottom: 1px solid #E0E0E0;
			gap: 6px;
		}
		.bar-wrapper { flex: 1; display: flex; justify-content: center; align-items: flex-end; height: 100%; }
		.bar {
			width: 60%;
			min-height: 2px;
			background: #41B8E6;
			border-top-left-radius: 6px;
			border-top-right-radius: 6px;
		}
		.x-axis { display: flex; gap: 6px; margin-top: 12px; }
		.x-label-wrapper { flex: 1; text-align: center; }
		.x-label { font-size: 13px; font-weight: 400; color: #616161; }

		/* Orientation-specific adjustments for one-per-page print */
		@media print and (orientation: portrait) {
			/* portrait: single-card centered vertically, constrained width */
			.opp-page { padding: 10mm; }
			.kpi-card-full { width: 100%; max-width: calc(210mm - 20mm); }
			.header-bar { width: 100%; }
		}

		@media print and (orientation: landscape) {
			/* landscape: fit the full card on one A4 landscape page */
			.opp-page { padding: 8mm; box-sizing: border-box; }
			.opp-card-center { align-items: flex-start; min-height: 0; }
			.kpi-card-full {
				width: 100%;
				max-width: calc(297mm - 16mm);
				box-sizing: border-box;
				page-break-inside: avoid;
				break-inside: avoid;
				-webkit-column-break-inside: avoid;
				padding: 6mm;
				display: flex;
				flex-direction: column;
			}
			/* compact header and title */
			.header-bar { width: 100%; margin-bottom: 6px; padding: 6px 8px; }
			.card-header { margin-bottom: 6px; }
			.badge { width: 26px; height: 26px; }
			.badge-text { font-size: 13px; }
			.card-title { font-size: 16px; }
			/* metrics: lower padding */
			.metrics { padding: 6px 6px; margin-bottom: 6px; gap: 8px; }
			.metric-label { font-size: 12px; margin-bottom: 4px; }
			.metric-value { font-size: 16px; }
			/* chart: keep aspect-ratio approach, do not override bars height */
			.chart { flex: 1; }
			.bars { gap: 4px; }
			.y-axis { margin-bottom: 18px; }
			/* Ensure printing engines avoid breaking inside these elements */
			.kpi-card-full, .opp-page, .opp-card-center { page-break-inside: avoid; break-inside: avoid; -webkit-column-break-inside: avoid; }
		}
	`;
};

const renderOnePerPageSparcCard = (card: SparcKpiPrintCard, date: string, logoBase64: string): string => {
	const logoImg = logoBase64
		? `<img class="logo" src="${logoBase64}" alt="Warehouse Navigator" />`
		: `<div class="logo" style="background: transparent;"></div>`;

	const ordered = getOrderedChartData(card.chartData || []);

	let maxValue = 10;
	for (const dp of ordered) {
		const v = Number(dp.value) || 0;
		if (v > maxValue) maxValue = v;
	}

	const barsParts: string[] = [];
	const xLabelParts: string[] = [];
	for (const dp of ordered) {
		const pct = maxValue > 0 ? (Number(dp.value) / maxValue) * 100 : 0;
		const safePct = Number.isFinite(pct) ? Math.max(0, pct) : 0;
		barsParts.push(`<div class="bar-wrapper"><div class="bar" style="height: ${safePct.toFixed(2)}%;"></div></div>`);
		xLabelParts.push(`<div class="x-label-wrapper"><span class="x-label">${escapeHtml(dp.date)}</span></div>`);
	}

	const isDeltaNegative = card.delta < 0;
	const isDeltaPositive = card.delta > 0;
	const deltaColor = isDeltaNegative ? '#D32F2F' : isDeltaPositive ? 'rgb(18, 159, 20)' : '#000000';
	const maxLabel = Number.isInteger(maxValue) ? String(maxValue) : maxValue.toFixed(2);

	return `
		<div class="opp-page">
			<div class="header-bar">
				<div class="header-left">
					${logoImg}
					<span class="header-title">Warehouse Navigator</span>
				</div>
				<span class="header-date">${escapeHtml(date)}</span>
			</div>
			<div class="section-row">
				<span class="section-title">Selected SPARC KPIs</span>
			</div>
			<div class="opp-card-center">
				<div class="kpi-card-full">
					<div class="card-header">
						<div class="card-header-left">
							<div class="badge" style="background:${escapeHtml(card.badgeColor || '#D4F4DD')}">
								<span class="badge-text">${escapeHtml(card.badgeLetter || '')}</span>
							</div>
							<span class="card-title">${escapeHtml(card.title || '')}</span>
						</div>
					</div>
					<div class="metrics">
						<div class="metric">
							<div class="metric-label">Actual</div>
							<div class="metric-value">${formatNumber(card.actual)}</div>
						</div>
						<div class="metric">
							<div class="metric-label">Target</div>
							<div class="metric-value">${formatNumber(card.target)}</div>
						</div>
						<div class="metric">
							<div class="metric-label">Delta</div>
							<div class="metric-value" style="color:${deltaColor}">${formatNumber(card.delta)}</div>
						</div>
					</div>
					<div class="chart">
						<div class="y-axis">
							<span class="y-label">${maxLabel}</span>
							<span class="y-label">0</span>
						</div>
						<div class="chart-content">
							<div class="bars">${barsParts.join('')}</div>
							<div class="x-axis">${xLabelParts.join('')}</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	`;
};

const renderOnePerPageLighthouseCard = (card: LighthouseKpiPrintCard, date: string, logoBase64: string): string => {
	const logoImg = logoBase64
		? `<img class="logo" src="${logoBase64}" alt="Warehouse Navigator" />`
		: `<div class="logo" style="background: transparent;"></div>`;

	const ordered = getOrderedChartData(card.chartData || []);

	let maxValue = 10;
	for (const dp of ordered) {
		const v = Number(dp.value) || 0;
		if (v > maxValue) maxValue = v;
	}

	const barsParts: string[] = [];
	const xLabelParts: string[] = [];
	for (const dp of ordered) {
		const pct = maxValue > 0 ? (Number(dp.value) / maxValue) * 100 : 0;
		const safePct = Number.isFinite(pct) ? Math.max(0, pct) : 0;
		barsParts.push(`<div class="bar-wrapper"><div class="bar" style="height: ${safePct.toFixed(2)}%;"></div></div>`);
		xLabelParts.push(`<div class="x-label-wrapper"><span class="x-label">${escapeHtml(dp.date)}</span></div>`);
	}

	const cwColor = getColorForScore(card.cw);
	const lcwColor = getColorForScore(card.lcw);
	const lcpColor = getColorForScore(card.lcp);
	const ytdColor = getColorForScore(card.ytd);
	const maxLabel = Number.isInteger(maxValue) ? String(maxValue) : maxValue.toFixed(2);

	return `
		<div class="opp-page">
			<div class="header-bar">
				<div class="header-left">
					${logoImg}
					<span class="header-title">Warehouse Navigator</span>
				</div>
				<span class="header-date">${escapeHtml(date)}</span>
			</div>
			<div class="section-row">
				<span class="section-title">Selected Lighthouse KPIs</span>
			</div>
			<div class="opp-card-center">
				<div class="kpi-card-full">
					<div class="card-header">
						<div class="card-header-left">
							<span class="card-title">${escapeHtml(card.title || '')}</span>
						</div>
					</div>
					<div class="metrics">
						<div class="metric">
							<div class="metric-label">CW</div>
							<div class="metric-value" style="color:${escapeHtml(cwColor)}">${escapeHtml(formatScoreDisplay(card.cw))}</div>
						</div>
						<div class="metric">
							<div class="metric-label">LCW</div>
							<div class="metric-value" style="color:${escapeHtml(lcwColor)}">${escapeHtml(formatScoreDisplay(card.lcw))}</div>
						</div>
						<div class="metric">
							<div class="metric-label">LCP</div>
							<div class="metric-value" style="color:${escapeHtml(lcpColor)}">${escapeHtml(formatScoreDisplay(card.lcp))}</div>
						</div>
						<div class="metric">
							<div class="metric-label">YTD</div>
							<div class="metric-value" style="color:${escapeHtml(ytdColor)}">${escapeHtml(formatScoreDisplay(card.ytd))}</div>
						</div>
					</div>
					<div class="chart">
						<div class="y-axis">
							<span class="y-label">${maxLabel}</span>
							<span class="y-label">0</span>
						</div>
						<div class="chart-content">
							<div class="bars">${barsParts.join('')}</div>
							<div class="x-axis">${xLabelParts.join('')}</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	`;
};

export const generateReviewProgressKpiOnePerPageHTML = (args: {
	date: string;
	logoBase64?: string;
	sparcCards: SparcKpiPrintCard[];
	lighthouseCards: LighthouseKpiPrintCard[];
}): string => {
	const sparcPages = (args.sparcCards || [])
		.map((card) => renderOnePerPageSparcCard(card, args.date, args.logoBase64 || ''))
		.join('');

	const lighthousePages = (args.lighthouseCards || [])
		.map((card) => renderOnePerPageLighthouseCard(card, args.date, args.logoBase64 || ''))
		.join('');

	return `<!DOCTYPE html>
<html lang="en">
	<head>
		<meta charset="UTF-8" />
		<meta name="viewport" content="width=device-width, initial-scale=1.0" />
		<title>Selected KPIs - One Per Page</title>
		<style>
			${renderOnePerPageStyles()}
		</style>
	</head>
	<body>
		${sparcPages}
		${lighthousePages}
	</body>
</html>`;
};

type ReviewProgressKpiPrintProps = {
	sparcCards: SparcKpiPrintCard[];
	lighthouseCards: LighthouseKpiPrintCard[];
};

const ReviewProgressKpiPrint = forwardRef(function ReviewProgressKpiPrint(
	props: ReviewProgressKpiPrintProps,
	ref: React.Ref<ReviewProgressKpiPrintRef>,
) {
	const iframeRef = useRef<HTMLIFrameElement | null>(null);
	const iframeOnePerPageRef = useRef<HTMLIFrameElement | null>(null);
	const [logoBase64, setLogoBase64] = useState<string>('');

	useEffect(() => {
		const loadLogo = async () => {
			if (Platform.OS !== 'web') return;
			try {
				const response = await fetch(warehouseNavigatorLogo);
				const blob = await response.blob();
				const reader = new FileReader();
				reader.onloadend = () => setLogoBase64(reader.result as string);
				reader.readAsDataURL(blob);
			} catch (e) {
				console.warn('Failed to load logo for print:', e);
				setLogoBase64('');
			}
		};
		loadLogo();
	}, []);

	const printDate = useMemo(
		() =>
			new Date().toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
			}),
		[],
	);

	const htmlContent = useMemo(
		() =>
			generateReviewProgressKpiPrintHTML({
				date: printDate,
				logoBase64,
				sparcCards: props.sparcCards || [],
				lighthouseCards: props.lighthouseCards || [],
			}),
		[printDate, logoBase64, props.sparcCards, props.lighthouseCards],
	);

	const htmlOnePerPageContent = useMemo(
		() =>
			generateReviewProgressKpiOnePerPageHTML({
				date: printDate,
				logoBase64,
				sparcCards: props.sparcCards || [],
				lighthouseCards: props.lighthouseCards || [],
			}),
		[printDate, logoBase64, props.sparcCards, props.lighthouseCards],
	);

	useImperativeHandle(
		ref,
		() => ({
			print: () => {
				if (Platform.OS === 'web') {
					const iframe = iframeRef.current;
					if (iframe?.contentWindow) {
						iframe.contentWindow.focus();
						iframe.contentWindow.print();
					}
					return;
				}

				console.log('Mobile print - use react-native-print with HTML:', htmlContent);
			},
			printOnePerPage: () => {
				if (Platform.OS === 'web') {
					const iframe = iframeOnePerPageRef.current;
					if (iframe?.contentWindow) {
						iframe.contentWindow.focus();
						iframe.contentWindow.print();
					}
					return;
				}

				console.log('Mobile print one per page - use react-native-print with HTML:', htmlOnePerPageContent);
			},
			getHtml: () => htmlContent,
		}),
		[htmlContent, htmlOnePerPageContent],
	);

	useEffect(() => {
		if (Platform.OS === 'web' && iframeRef.current?.contentDocument) {
			const doc = iframeRef.current.contentDocument;
			doc.open();
			doc.write(htmlContent);
			doc.close();
		}
	}, [htmlContent]);

	useEffect(() => {
		if (Platform.OS === 'web' && iframeOnePerPageRef.current?.contentDocument) {
			const doc = iframeOnePerPageRef.current.contentDocument;
			doc.open();
			doc.write(htmlOnePerPageContent);
			doc.close();
		}
	}, [htmlOnePerPageContent]);

	if (Platform.OS !== 'web') {
		return <View style={styles.hiddenContainer} />;
	}

	return (
		<View style={styles.hiddenContainer}>
			{/* @ts-ignore - iframe is web-only */}
			<iframe
				ref={iframeRef}
				style={{
					position: 'absolute',
					width: '1px',
					height: '1px',
					opacity: 0,
					pointerEvents: 'none',
				}}
				title="Selected KPI Print"
			/>
			{/* @ts-ignore - iframe is web-only */}
			<iframe
				ref={iframeOnePerPageRef}
				style={{
					position: 'absolute',
					width: '1px',
					height: '1px',
					opacity: 0,
					pointerEvents: 'none',
				}}
				title="Selected KPI One Per Page Print"
			/>
		</View>
	);
});

const styles = StyleSheet.create({
	hiddenContainer: {
		position: 'absolute',
		left: -9999,
		top: -9999,
		width: 1,
		height: 1,
		opacity: 0,
	},
});

ReviewProgressKpiPrint.displayName = 'ReviewProgressKpiPrint';
(ReviewProgressKpiPrint as any).PureComponent = true;

export default ReviewProgressKpiPrint;
